import { useMemo, useState } from 'react';
import type { CheckoutPlatform, PaymentOptions, ProductCard } from '../../api/marketplace';
import { PaymentSheet } from '../checkout/PaymentSheet';
import { notifyPayResult, payErrorMessage, payItems } from '../checkout/pay';
import { StarIcon } from '../tabs/icons';
import { haptic, hapticImpact } from '../../lib/telegram';
import {
  formatTopUpRange,
  isValidTopUpAmount,
  rubToStars,
  topUpCommission,
  topUpPayRange,
  topUpTotal,
} from '../../lib/top-up';
import './TopUpBlock.css';

interface TopUpBlockProps {
  products: ProductCard[];
  payments: PaymentOptions;
  onPurchased: (orderId?: string) => void;
}

export function TopUpBlock({ products, payments, onPurchased }: TopUpBlockProps) {
  const [selectedId, setSelectedId] = useState(products[0]?.id ?? '');
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payOpen, setPayOpen] = useState(false);

  const product = products.find((item) => item.id === selectedId) ?? products[0] ?? null;
  const amountRaw = product ? (amounts[product.id] ?? '') : '';
  const amount = Number(amountRaw);
  const valid = product ? isValidTopUpAmount(amount, product) : false;
  const bps = product?.commissionBps ?? 0;
  const minorPerStar = payments.minorPerStar ?? 200;
  const range = product ? topUpPayRange(product) : { min: 1, max: 10_000_000 };

  const quote = useMemo(() => {
    if (!valid) return { commission: 0, total: 0, stars: 0 };
    const commission = topUpCommission(amount, bps);
    const total = topUpTotal(amount, bps);
    return { commission, total, stars: rubToStars(total, minorPerStar) };
  }, [amount, bps, minorPerStar, valid]);

  if (!product) return null;

  const pay = async (platform: CheckoutPlatform): Promise<void> => {
    if (!valid) return;
    setBusy(true);
    setError(null);

    try {
      const outcome = await payItems(
        [{ productId: product.id, quantity: 1, topUpAmount: amount }],
        platform,
      );
      const message = notifyPayResult(outcome.result);
      if (message) setError(message);
      if (outcome.result === 'paid' || outcome.result === 'pending') onPurchased(outcome.orderId);
    } catch (cause) {
      haptic('error');
      setError(payErrorMessage(cause));
    } finally {
      setBusy(false);
      setPayOpen(false);
    }
  };

  return (
    <article className="topup-row">
      <div className="topup-row__tabs" role="tablist" aria-label="Сервис пополнения">
        {products.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={item.id === product.id}
            className={`chip${item.id === product.id ? ' chip--active' : ''}`}
            onClick={() => {
              if (item.id === product.id) return;
              hapticImpact('light');
              setSelectedId(item.id);
              setError(null);
            }}
          >
            {item.title}
          </button>
        ))}
      </div>

      <div className="topup-row__head">
        <p className="topup-row__range">{formatTopUpRange(product)}</p>
        {bps > 0 ? <p className="topup-row__commission">комиссия {bps / 100}%</p> : null}
      </div>

      {product.description ? <p className="topup-row__hint">{product.description}</p> : null}

      <label className="topup-row__field">
        <span className="topup-row__label">Сумма, ₽</span>
        <span className="topup-row__input-wrap">
          <input
            className="topup-row__input"
            type="number"
            inputMode="numeric"
            min={range.min}
            max={range.max}
            placeholder={String(range.min)}
            value={amountRaw}
            onChange={(event) => {
              const next = event.target.value.replace(/[^\d]/g, '');
              setAmounts((current) => ({ ...current, [product.id]: next }));
            }}
          />
          <span className="topup-row__suffix">₽</span>
        </span>
      </label>

      {valid ? (
        <dl className="topup-row__quote">
          <div>
            <dt>{product.title}</dt>
            <dd>{amount} ₽</dd>
          </div>
          <div>
            <dt>Комиссия{bps > 0 ? ` ${bps / 100}%` : ''}</dt>
            <dd>{quote.commission} ₽</dd>
          </div>
          <div className="topup-row__total">
            <dt>Итого</dt>
            <dd>
              {quote.total} ₽
              <span>
                <StarIcon size={14} /> {quote.stars}
              </span>
            </dd>
          </div>
        </dl>
      ) : (
        <p className="topup-row__hint">
          {amountRaw
            ? `Для ${product.title}: ${formatTopUpRange(product)}`
            : 'Введи сумму — комиссия посчитается сразу'}
        </p>
      )}

      {error ? <p className="topup-row__error">{error}</p> : null}

      <button
        type="button"
        className="topup-row__buy"
        disabled={!product.inStock || busy || !valid}
        onClick={() => setPayOpen(true)}
      >
        Приобрести
      </button>

      <PaymentSheet
        open={payOpen}
        payments={{ ...payments, tribute: payments.tribute || Boolean(product.hasTribute) }}
        busy={busy}
        starsAmount={quote.stars}
        rubAmount={quote.total}
        onClose={() => setPayOpen(false)}
        onPay={(platform) => void pay(platform)}
      />
    </article>
  );
}
